import React from 'react';
import { Calendar } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

export const NewProjectDialog = ({ open, onOpenChange, newProject, setNewProject, creating, onSubmit }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-2xl border border-zinc-200 rounded-none p-0 max-h-[90vh] overflow-y-auto">
      <DialogHeader className="p-6 pb-0">
        <DialogTitle className="font-heading text-xl font-bold uppercase tracking-wide text-zinc-900">New Project</DialogTitle>
      </DialogHeader>

      <form onSubmit={onSubmit} className="p-6 pt-4 space-y-6">
        {/* Basic Info */}
        <div className="space-y-4">
          <h3 className="text-xs font-heading font-semibold uppercase tracking-wider text-zinc-400">Basic Information</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 sm:col-span-1">
              <label className="block text-xs font-bold uppercase tracking-wide mb-2">
                Project Name *
              </label>
              <input
                type="text"
                value={newProject.name}
                onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                required
                data-testid="new-project-name"
                className="w-full h-11 border border-zinc-200 bg-white text-zinc-900 text-sm px-4 focus:outline-none focus:border-primary transition-colors"
              />
            </div>

            <div className="col-span-2 sm:col-span-1">
              <label className="block text-xs font-bold uppercase tracking-wide mb-2">
                Client Name *
              </label>
              <input
                type="text"
                value={newProject.client_name}
                onChange={(e) => setNewProject({ ...newProject, client_name: e.target.value })}
                required
                data-testid="new-project-client"
                className="w-full h-14 border border-zinc-200 bg-white text-zinc-900 text-lg p-4 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                placeholder="ABC Development Corp"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-wide mb-2">
              Project Type
            </label>
            <select
              value={newProject.project_type}
              onChange={(e) => setNewProject({ ...newProject, project_type: e.target.value })}
              data-testid="new-project-type"
              className="w-full h-14 border border-zinc-200 bg-white text-zinc-900 text-lg p-4 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            >
              <option value="Electrical">Electrical</option>
              <option value="Plumbing">Plumbing</option>
              <option value="HVAC">HVAC</option>
              <option value="Mechanical">Mechanical</option>
              <option value="Multi-Trade">Multi-Trade</option>
              <option value="Other">Other</option>
            </select>
          </div>
        </div>

        {/* Address */}
        <div className="space-y-4">
          <h3 className="text-xs font-heading font-semibold uppercase tracking-wider text-zinc-400">Job Site Address</h3>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wide mb-2">
              Street Address
            </label>
            <input
              type="text"
              value={newProject.street}
              onChange={(e) => setNewProject({ ...newProject, street: e.target.value })}
              data-testid="new-project-street"
              className="w-full h-14 border border-zinc-200 bg-white text-zinc-900 text-lg p-4 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              placeholder="123 Main Street"
            />
          </div>
          <div className="grid grid-cols-4 gap-4">
            <div className="col-span-2">
              <label className="block text-xs font-bold uppercase tracking-wide mb-2">City</label>
              <input
                type="text"
                value={newProject.city}
                onChange={(e) => setNewProject({ ...newProject, city: e.target.value })}
                className="w-full h-14 border border-zinc-200 bg-white text-zinc-900 text-lg p-4 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                placeholder="Los Angeles"
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wide mb-2">State</label>
              <input
                type="text"
                value={newProject.state}
                onChange={(e) => setNewProject({ ...newProject, state: e.target.value })}
                className="w-full h-14 border border-zinc-200 bg-white text-zinc-900 text-lg p-4 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                placeholder="CA"
                maxLength={2}
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wide mb-2">ZIP</label>
              <input
                type="text"
                value={newProject.zip}
                onChange={(e) => setNewProject({ ...newProject, zip: e.target.value })}
                className="w-full h-14 border border-zinc-200 bg-white text-zinc-900 text-lg p-4 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                placeholder="90001"
              />
            </div>
          </div>
        </div>

        {/* Schedule & Budget */}
        <div className="space-y-4">
          <h3 className="text-xs font-heading font-semibold uppercase tracking-wider text-zinc-400">Schedule & Budget</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wide mb-2">Start Date</label>
              <div className="relative">
                <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500 pointer-events-none" />
                <input
                  type="date"
                  value={newProject.start_date}
                  onChange={(e) => setNewProject({ ...newProject, start_date: e.target.value })}
                  data-testid="new-project-start"
                  className="w-full h-14 border border-zinc-200 bg-white text-zinc-900 text-lg pl-12 pr-4 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wide mb-2">End Date</label>
              <div className="relative">
                <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500 pointer-events-none" />
                <input
                  type="date"
                  value={newProject.end_date}
                  onChange={(e) => setNewProject({ ...newProject, end_date: e.target.value })}
                  data-testid="new-project-end"
                  className="w-full h-14 border border-zinc-200 bg-white text-zinc-900 text-lg pl-12 pr-4 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                />
              </div>
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wide mb-2">
              Total Materials Budget
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-lg font-bold text-zinc-500">$</span>
              <input
                type="number"
                value={newProject.total_budget}
                onChange={(e) => setNewProject({ ...newProject, total_budget: e.target.value })}
                data-testid="new-project-budget"
                className="w-full h-14 border border-zinc-200 bg-white text-zinc-900 text-lg pl-10 pr-4 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                placeholder="250,000"
                min="0"
                step="1000"
              />
            </div>
          </div>
        </div>

        <div className="flex gap-3 pt-4">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="flex-1 h-14 bg-white text-zinc-900 font-bold uppercase tracking-wide border border-zinc-200 hover:bg-zinc-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={creating}
            data-testid="create-project-btn"
            className="flex-1 h-14 bg-primary text-zinc-900 font-bold uppercase tracking-wide border border-zinc-200  hover:bg-primary-hover active:translate-y-1 active: transition-all disabled:opacity-50"
          >
            {creating ? 'Creating...' : 'Create Project'}
          </button>
        </div>
      </form>
    </DialogContent>
  </Dialog>
);
